import { useState, useEffect, useMemo } from "react";
import {
  Plus, Search, Pencil, Users, Building2, User, Store,
  Power, PowerOff, Phone, Mail, Contact, FileText, Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { partiesApi, customerPaymentsApi, expensesApi } from "@/lib/unifiedApi";
import { subscribeDocuments, DOC_TYPES, DOC_STATUSES } from "@/lib/documentsApi";
import { logActivity } from "@/lib/activityLog";
import { useAuth } from "@/context/AuthContext";

const fmtB = (n) => Number(n || 0).toLocaleString("th-TH", { minimumFractionDigits: 2 });

// ─── Constants ──────────────────────────────────────────────
const WHT_RATES = ["", "1", "2", "3", "5", "10"];

const EMPTY_FORM = {
  types: ["customer"],
  name: "",
  is_company: "company",       // company | individual
  tax_id: "",
  branch: "00000",
  address: "",
  phone: "",
  email: "",
  contact_person: "",
  default_wht_rate: "",
  bank_name: "",
  bank_account_no: "",
  bank_account_name: "",
  note: "",
  active: true,
};

function toForm(p) {
  if (!p) return EMPTY_FORM;
  return {
    types: p.types?.length ? p.types : ["customer"],
    name: p.name ?? "",
    is_company: p.is_company ?? "company",
    tax_id: p.tax_id ?? "",
    branch: p.branch ?? "00000",
    address: p.address ?? "",
    phone: p.phone ?? "",
    email: p.email ?? "",
    contact_person: p.contact_person ?? "",
    default_wht_rate: p.default_wht_rate != null ? String(p.default_wht_rate) : "",
    bank_name: p.bank?.bank ?? "",
    bank_account_no: p.bank?.account_no ?? "",
    bank_account_name: p.bank?.account_name ?? "",
    note: p.note ?? "",
    active: p.active !== false,
  };
}

// ─── Type badges ────────────────────────────────────────────
function TypeBadges({ types = [] }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {types.includes("customer") && (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 border border-blue-200 flex items-center gap-1 w-fit whitespace-nowrap">
          <User size={10} />ลูกค้า
        </span>
      )}
      {types.includes("vendor") && (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-600 border border-amber-200 flex items-center gap-1 w-fit whitespace-nowrap">
          <Store size={10} />ผู้ขาย
        </span>
      )}
    </div>
  );
}

// ─── Add/Edit dialog ────────────────────────────────────────
function PartyDialog({ open, onClose, party, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  useEffect(() => { setForm(toForm(party)); }, [party, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleType = (t) =>
    setForm((f) => {
      const has = f.types.includes(t);
      const types = has ? f.types.filter((x) => x !== t) : [...f.types, t];
      return { ...f, types };
    });

  async function save() {
    if (!form.name.trim()) { toast.error("กรุณากรอกชื่อคู่ค้า"); return; }
    if (!form.types.length) { toast.error("เลือกอย่างน้อย 1 ประเภท (ลูกค้า/ผู้ขาย)"); return; }
    if (form.tax_id && !/^\d{13}$/.test(form.tax_id.replace(/[- ]/g, ""))) {
      toast.error("เลขผู้เสียภาษีต้องเป็นตัวเลข 13 หลัก");
      return;
    }
    setSaving(true);
    try {
      const data = {
        types: form.types,
        name: form.name.trim(),
        is_company: form.is_company,
        tax_id: form.tax_id.replace(/[- ]/g, ""),
        branch: form.branch.trim(),
        address: form.address.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        contact_person: form.contact_person.trim(),
        default_wht_rate: form.default_wht_rate ? Number(form.default_wht_rate) : null,
        bank: {
          bank: form.bank_name.trim(),
          account_no: form.bank_account_no.trim(),
          account_name: form.bank_account_name.trim(),
        },
        note: form.note.trim(),
        active: form.active,
      };
      if (party?.id) {
        await partiesApi.update(party.id, data);
        logActivity(user?.id, user?.name, "party_update", `แก้ไขคู่ค้า ${data.name}`);
        toast.success("บันทึกแล้ว");
      } else {
        const res = await partiesApi.createWithCode(data);
        logActivity(user?.id, user?.name, "party_create", `เพิ่มคู่ค้า ${data.name} (${res?.data?.code || ""})`);
        toast.success(`เพิ่มคู่ค้าแล้ว ${res?.data?.code || ""}`);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error("บันทึกไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{party?.id ? "แก้ไขคู่ค้า" : "เพิ่มคู่ค้า"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Type */}
          <div className="space-y-2">
            <Label className="text-slate-500">ประเภท</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => toggleType("customer")}
                className={`flex-1 py-2 rounded-lg border text-sm font-medium flex items-center justify-center gap-1.5 ${
                  form.types.includes("customer")
                    ? "bg-blue-50 text-blue-600 border-blue-300"
                    : "bg-slate-100 text-slate-400 border-slate-200"
                }`}
              >
                <User size={14} />ลูกค้า
              </button>
              <button
                type="button"
                onClick={() => toggleType("vendor")}
                className={`flex-1 py-2 rounded-lg border text-sm font-medium flex items-center justify-center gap-1.5 ${
                  form.types.includes("vendor")
                    ? "bg-amber-50 text-amber-600 border-amber-300"
                    : "bg-slate-100 text-slate-400 border-slate-200"
                }`}
              >
                <Store size={14} />ผู้ขาย
              </button>
            </div>
          </div>

          {/* Name + entity type */}
          <div className="space-y-2">
            <Label className="text-slate-500">ชื่อ <span className="text-red-400">*</span></Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)}
              placeholder="ชื่อบริษัท หรือ ชื่อบุคคล"
              className="bg-white border-slate-200 text-slate-800" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-slate-500">ประเภทผู้เสียภาษี</Label>
              <Select value={form.is_company} onValueChange={(v) => set("is_company", v)}>
                <SelectTrigger className="bg-white border-slate-200 text-slate-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-800">
                  <SelectItem value="company">นิติบุคคล</SelectItem>
                  <SelectItem value="individual">บุคคลธรรมดา</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-500">WHT เริ่มต้น (%)</Label>
              <Select value={form.default_wht_rate || "none"} onValueChange={(v) => set("default_wht_rate", v === "none" ? "" : v)}>
                <SelectTrigger className="bg-white border-slate-200 text-slate-800"><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-800">
                  <SelectItem value="none">ไม่ระบุ</SelectItem>
                  {WHT_RATES.filter(Boolean).map((r) => (
                    <SelectItem key={r} value={r}>{r}%</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2 col-span-2">
              <Label className="text-slate-500">เลขผู้เสียภาษี (13 หลัก)</Label>
              <Input value={form.tax_id} onChange={(e) => set("tax_id", e.target.value)}
                placeholder="0000000000000" inputMode="numeric"
                className="bg-white border-slate-200 text-slate-800" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-500">สาขา</Label>
              <Input value={form.branch} onChange={(e) => set("branch", e.target.value)}
                placeholder="00000"
                className="bg-white border-slate-200 text-slate-800" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-500">ที่อยู่</Label>
            <textarea value={form.address} onChange={(e) => set("address", e.target.value)}
              rows={2} placeholder="ที่อยู่สำหรับออกเอกสาร"
              className="w-full rounded-md bg-white border border-slate-200 text-slate-800 text-sm p-2 resize-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-slate-500">เบอร์โทร</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)}
                className="bg-white border-slate-200 text-slate-800" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-500">อีเมล</Label>
              <Input value={form.email} onChange={(e) => set("email", e.target.value)}
                className="bg-white border-slate-200 text-slate-800" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-500">ผู้ติดต่อ</Label>
            <Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)}
              className="bg-white border-slate-200 text-slate-800" />
          </div>

          {/* Bank (mainly for vendors) */}
          <div className="space-y-2 border-t border-slate-200 pt-3">
            <Label className="text-slate-500 text-xs">บัญชีธนาคาร (สำหรับจ่ายเงินผู้ขาย)</Label>
            <div className="grid grid-cols-3 gap-2">
              <Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)}
                placeholder="ธนาคาร" className="bg-white border-slate-200 text-slate-800" />
              <Input value={form.bank_account_no} onChange={(e) => set("bank_account_no", e.target.value)}
                placeholder="เลขบัญชี" className="bg-white border-slate-200 text-slate-800" />
              <Input value={form.bank_account_name} onChange={(e) => set("bank_account_name", e.target.value)}
                placeholder="ชื่อบัญชี" className="bg-white border-slate-200 text-slate-800" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-500">หมายเหตุ</Label>
            <Input value={form.note} onChange={(e) => set("note", e.target.value)}
              className="bg-white border-slate-200 text-slate-800" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-500">ยกเลิก</Button>
          <Button onClick={save} disabled={saving}
            style={{ backgroundColor: "#2563eb", color: "#fff" }}>
            {saving ? "กำลังบันทึก..." : "บันทึก"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Due date for an invoice: explicit due_date, else issue_date + credit_days.
function invoiceDue(d) {
  if (d.due_date) return new Date(d.due_date);
  if (d.issue_date) {
    const base = new Date(d.issue_date);
    base.setDate(base.getDate() + (Number(d.credit_days) || 0));
    return base;
  }
  return null;
}

// FIFO-apply received payments to invoices (oldest due first) and bucket the
// still-unpaid remainder by age. Approximate until per-invoice allocation (Phase 2C).
function agingBuckets(invoices, received) {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const sorted = invoices
    .slice()
    .sort((a, b) => (invoiceDue(a)?.getTime() || 0) - (invoiceDue(b)?.getTime() || 0));
  let left = received;
  const b = { notDue: 0, d30: 0, d60: 0, d60plus: 0 };
  for (const inv of sorted) {
    const amt = Number(inv.total || 0);
    const applied = Math.min(left, amt);
    left -= applied;
    const unpaid = amt - applied;
    if (unpaid <= 0.005) continue;
    const due = invoiceDue(inv);
    const overdue = due ? Math.floor((now - due) / 86400000) : 0;
    if (overdue <= 0) b.notDue += unpaid;
    else if (overdue <= 30) b.d30 += unpaid;
    else if (overdue <= 60) b.d60 += unpaid;
    else b.d60plus += unpaid;
  }
  return b;
}

// ─── AR / AP / history drawer ───────────────────────────────
function PartyDrawer({ party, docs, payments, expenses, onClose }) {
  if (!party) return null;
  const isCustomer = (party.types || []).includes("customer");
  const isVendor = (party.types || []).includes("vendor");

  const myDocs = docs.filter((d) => d.party_id === party.id);
  const myPays = payments.filter((p) => p.party_id === party.id);
  const myExpenses = (expenses || []).filter((e) => e.party_id === party.id);

  // AR: billed on invoices/tax-invoices − confirmed receipts
  const invoices = myDocs.filter((d) => ["IV", "TI"].includes(d.doc_type) && d.status !== "void");
  const billed = invoices.reduce((s, d) => s + Number(d.total || 0), 0);
  const received = myPays
    .filter((p) => p.status === "confirmed")
    .reduce((s, p) => s + Number(p.amount || 0), 0);
  const outstanding = billed - received;
  const aging = agingBuckets(invoices, received);

  // AP: total paid to this vendor (confirmed expenses)
  const apTotal = myExpenses
    .filter((e) => e.status !== "rejected")
    .reduce((s, e) => s + Number(e.amount || 0), 0);

  return (
    <Dialog open={!!party} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {party.is_company === "individual" ? <User size={18} /> : <Building2 size={18} />}
            {party.name}
          </DialogTitle>
        </DialogHeader>

        <div className="text-slate-400 text-xs -mt-2">
          {party.code}{party.tax_id ? ` · ${party.tax_id}` : ""}
          {party.phone ? ` · ${party.phone}` : ""}
        </div>

        {isCustomer && (
          <>
            <div className="grid grid-cols-3 gap-3 mt-2">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <div className="text-slate-400 text-xs">ยอดวางบิล (IV/TI)</div>
                <div className="text-lg font-bold text-slate-800 mt-1">฿{fmtB(billed)}</div>
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <div className="text-slate-400 text-xs">รับชำระแล้ว</div>
                <div className="text-lg font-bold text-emerald-600 mt-1">฿{fmtB(received)}</div>
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <div className="text-slate-400 text-xs">คงค้าง (AR)</div>
                <div className={`text-lg font-bold mt-1 ${outstanding > 0 ? "text-amber-600" : "text-slate-500"}`}>฿{fmtB(outstanding)}</div>
              </div>
            </div>

            {/* AR aging */}
            {outstanding > 0.005 && (
              <div className="mt-3">
                <div className="text-slate-500 text-sm font-medium mb-1.5">อายุหนี้ค้างรับ (aging)</div>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { k: "notDue", label: "ยังไม่ครบกำหนด", val: aging.notDue, cls: "text-slate-600" },
                    { k: "d30", label: "1-30 วัน", val: aging.d30, cls: "text-amber-600" },
                    { k: "d60", label: "31-60 วัน", val: aging.d60, cls: "text-orange-600" },
                    { k: "d60plus", label: "60+ วัน", val: aging.d60plus, cls: "text-red-600" },
                  ].map((b) => (
                    <div key={b.k} className="rounded-lg border border-slate-200 p-2 text-center">
                      <div className="text-[10px] text-slate-400">{b.label}</div>
                      <div className={`text-sm font-bold mt-0.5 ${b.val > 0.005 ? b.cls : "text-slate-300"}`}>฿{fmtB(b.val)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Documents */}
            <div className="mt-2">
              <div className="text-slate-500 text-sm font-medium flex items-center gap-1.5 mb-1">
                <FileText size={14} />เอกสาร ({myDocs.length})
              </div>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                {myDocs.length === 0 ? (
                  <div className="px-3 py-4 text-center text-slate-400 text-sm">ยังไม่มีเอกสารที่ผูกกับคู่ค้านี้</div>
                ) : (
                  <table className="w-full text-sm">
                    <tbody>
                      {myDocs
                        .slice()
                        .sort((a, b) => (b.issue_date || "").localeCompare(a.issue_date || ""))
                        .map((d) => (
                          <tr key={d.id} className="border-b border-slate-200/60 last:border-0">
                            <td className="px-3 py-2 font-mono text-xs text-slate-500">{d.doc_number}</td>
                            <td className="px-3 py-2 text-slate-600">{DOC_TYPES[d.doc_type]?.label || d.doc_type}</td>
                            <td className="px-3 py-2 text-slate-400 text-xs">{d.issue_date || "-"}</td>
                            <td className="px-3 py-2 text-right text-slate-800">฿{fmtB(d.total)}</td>
                            <td className="px-3 py-2 text-xs" style={{ color: DOC_STATUSES[d.status]?.color }}>
                              {DOC_STATUSES[d.status]?.label || d.status}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Payments */}
            <div className="mt-3">
              <div className="text-slate-500 text-sm font-medium flex items-center gap-1.5 mb-1">
                <Wallet size={14} />เงินที่รับ ({myPays.length})
              </div>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                {myPays.length === 0 ? (
                  <div className="px-3 py-4 text-center text-slate-400 text-sm">ยังไม่มีเงินรับที่ผูกกับคู่ค้านี้ — ผูกได้ที่หน้า Customer Pay</div>
                ) : (
                  <table className="w-full text-sm">
                    <tbody>
                      {myPays
                        .slice()
                        .sort((a, b) => (b.slip_date || "").localeCompare(a.slip_date || ""))
                        .map((p) => (
                          <tr key={p.id} className="border-b border-slate-200/60 last:border-0">
                            <td className="px-3 py-2 text-slate-400 text-xs">{p.slip_date || "-"}</td>
                            <td className="px-3 py-2 text-slate-600 text-xs">
                              {p.payment_type === "deposit" ? "มัดจำ" : p.payment_type === "remaining" ? "ส่วนที่เหลือ" : "-"}
                            </td>
                            <td className="px-3 py-2 text-right text-emerald-600">฿{fmtB(p.amount)}</td>
                            <td className="px-3 py-2 text-xs text-slate-400">
                              {p.status === "confirmed" ? "ยืนยันแล้ว" : p.status === "pending" ? "รอยืนยัน" : "ปฏิเสธ"}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}

        {isVendor && (
          <div className={isCustomer ? "mt-4 pt-4 border-t border-slate-200" : "mt-2"}>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 mb-2">
              <div className="text-slate-400 text-xs">รายจ่ายให้ผู้ขายรายนี้ (AP)</div>
              <div className="text-lg font-bold text-slate-800 mt-1">฿{fmtB(apTotal)}</div>
            </div>
            <div className="text-slate-500 text-sm font-medium flex items-center gap-1.5 mb-1">
              <Store size={14} />ค่าใช้จ่าย ({myExpenses.length})
            </div>
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              {myExpenses.length === 0 ? (
                <div className="px-3 py-4 text-center text-slate-400 text-sm">ยังไม่มีค่าใช้จ่ายที่ผูกกับผู้ขายนี้ — ผูกได้ที่หน้า Expenses</div>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {myExpenses
                      .slice()
                      .sort((a, b) => (b.slip_date || "").localeCompare(a.slip_date || ""))
                      .map((e) => (
                        <tr key={e.id} className="border-b border-slate-200/60 last:border-0">
                          <td className="px-3 py-2 text-slate-400 text-xs">{e.slip_date || "-"}</td>
                          <td className="px-3 py-2 text-slate-600 text-xs">{e.category || "-"}</td>
                          <td className="px-3 py-2 text-right text-slate-800">฿{fmtB(e.amount)}</td>
                          <td className="px-3 py-2 text-xs text-slate-400">
                            {e.status === "confirmed" ? "ยืนยันแล้ว" : e.status === "pending" ? "รอยืนยัน" : "ปฏิเสธ"}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ───────────────────────────────────────────────────
export default function PartiesPage() {
  const { isAdmin, user } = useAuth();
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");   // all | customer | vendor
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [drawerParty, setDrawerParty] = useState(null);
  const [docs, setDocs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);

  useEffect(() => {
    const unsub = partiesApi.subscribe((data) => {
      data.sort((a, b) => (a.code || "").localeCompare(b.code || ""));
      setParties(data);
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  // Documents + customer payments for the AR drawer (admin-only page)
  useEffect(() => {
    const unsubDocs = subscribeDocuments((d) => setDocs(d));
    const unsubPays = customerPaymentsApi.subscribe((p) => setPayments(p), () => {});
    const unsubExp = expensesApi.subscribe((e) => setExpenses(e), () => {});
    return () => { unsubDocs(); unsubPays(); unsubExp(); };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return parties.filter((p) => {
      if (!showInactive && p.active === false) return false;
      if (typeFilter !== "all" && !(p.types || []).includes(typeFilter)) return false;
      if (!q) return true;
      return (
        (p.name || "").toLowerCase().includes(q) ||
        (p.code || "").toLowerCase().includes(q) ||
        (p.tax_id || "").includes(q) ||
        (p.phone || "").includes(q) ||
        (p.contact_person || "").toLowerCase().includes(q)
      );
    });
  }, [parties, search, typeFilter, showInactive]);

  const counts = useMemo(() => {
    const active = parties.filter((p) => p.active !== false);
    return {
      customers: active.filter((p) => (p.types || []).includes("customer")).length,
      vendors: active.filter((p) => (p.types || []).includes("vendor")).length,
      total: active.length,
    };
  }, [parties]);

  async function toggleActive(p) {
    try {
      await partiesApi.update(p.id, { active: p.active === false });
      logActivity(
        user?.id, user?.name, "party_update",
        `${p.active === false ? "เปิดใช้งาน" : "ปิดใช้งาน"}คู่ค้า ${p.name}`
      );
      toast.success(p.active === false ? "เปิดใช้งานแล้ว" : "ปิดใช้งานแล้ว");
    } catch {
      toast.error("ทำรายการไม่สำเร็จ");
    }
  }

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-full py-24">
        <p className="text-slate-500">คุณไม่มีสิทธิ์เข้าถึงหน้านี้</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Users size={24} className="text-blue-600" />ทะเบียนคู่ค้า
          </h1>
          <p className="text-slate-400 text-sm mt-0.5">ลูกค้าและผู้ขาย — ใช้ร่วมกับ Billing และการเงิน</p>
        </div>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }}
          style={{ backgroundColor: "#2563eb", color: "#fff" }} className="font-semibold">
          <Plus size={16} className="mr-1" />เพิ่มคู่ค้า
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <div className="text-slate-400 text-xs">ลูกค้า</div>
          <div className="text-2xl font-bold text-blue-600 mt-1">{counts.customers}</div>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <div className="text-slate-400 text-xs">ผู้ขาย</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{counts.vendors}</div>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <div className="text-slate-400 text-xs">ทั้งหมด</div>
          <div className="text-2xl font-bold text-slate-800 mt-1">{counts.total}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="ค้นหา ชื่อ / รหัส / เลขภาษี / เบอร์"
            className="pl-9 bg-white border-slate-200 text-slate-800" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-36 bg-white border-slate-200 text-slate-800"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-slate-200 text-slate-800">
            <SelectItem value="all">ทุกประเภท</SelectItem>
            <SelectItem value="customer">ลูกค้า</SelectItem>
            <SelectItem value="vendor">ผู้ขาย</SelectItem>
          </SelectContent>
        </Select>
        <button
          onClick={() => setShowInactive((v) => !v)}
          className={`px-3 py-2 rounded-md text-sm border ${
            showInactive ? "bg-blue-50 text-blue-600 border-blue-200" : "bg-white text-slate-400 border-slate-200"
          }`}
        >
          แสดงที่ปิดใช้งาน
        </button>
      </div>

      {/* Table */}
      <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-200">
                <th className="px-4 py-3 font-medium">รหัส</th>
                <th className="px-4 py-3 font-medium">ชื่อ</th>
                <th className="px-4 py-3 font-medium">ประเภท</th>
                <th className="px-4 py-3 font-medium">เลขภาษี</th>
                <th className="px-4 py-3 font-medium">ติดต่อ</th>
                <th className="px-4 py-3 font-medium text-right">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">กำลังโหลด...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">ยังไม่มีคู่ค้า — กด "เพิ่มคู่ค้า" เพื่อเริ่มต้น</td></tr>
              ) : (
                filtered.map((p) => (
                  <tr key={p.id} className={`border-b border-slate-200/60 hover:bg-slate-50 ${p.active === false ? "opacity-50" : ""}`}>
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs whitespace-nowrap">{p.code || "-"}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => setDrawerParty(p)}
                        className="text-slate-800 font-medium flex items-center gap-1.5 hover:text-blue-600 text-left">
                        {p.is_company === "individual"
                          ? <User size={13} className="text-slate-400" />
                          : <Building2 size={13} className="text-slate-400" />}
                        {p.name}
                      </button>
                      {p.contact_person && (
                        <div className="text-slate-400 text-xs flex items-center gap-1 mt-0.5">
                          <Contact size={10} />{p.contact_person}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3"><TypeBadges types={p.types} /></td>
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs whitespace-nowrap">
                      {p.tax_id || "-"}{p.branch && p.branch !== "00000" ? ` (${p.branch})` : ""}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {p.phone && <div className="flex items-center gap-1"><Phone size={10} />{p.phone}</div>}
                      {p.email && <div className="flex items-center gap-1"><Mail size={10} />{p.email}</div>}
                      {!p.phone && !p.email && "-"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => { setEditing(p); setDialogOpen(true); }}
                          className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" title="แก้ไข">
                          <Pencil size={15} />
                        </button>
                        <button onClick={() => toggleActive(p)}
                          className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
                          title={p.active === false ? "เปิดใช้งาน" : "ปิดใช้งาน"}>
                          {p.active === false ? <Power size={15} className="text-green-400" /> : <PowerOff size={15} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <PartyDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        party={editing}
        onSaved={() => {}}
      />

      <PartyDrawer
        party={drawerParty}
        docs={docs}
        payments={payments}
        expenses={expenses}
        onClose={() => setDrawerParty(null)}
      />
    </div>
  );
}
